import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { authAPI } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

/**
 * "Can't access your email anymore?" multi-step recovery flow.
 *
 * Step 1 — collect first_name, last_name, city, province, phone, secondary_email
 * Step 2 — enter the 6-digit code emailed to that secondary address
 * Step 3 — confirm "make this my new primary email" or verify a different alternate
 *
 * The backend never tells us whether the identity match succeeded (anti-
 * enumeration), so we always advance to step 2 after submit. Bad inputs
 * fail at the /verify step with a generic "code is invalid" message.
 */
export default function AccountRecoveryFlow({ onBack }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1 fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [phone, setPhone] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');

  // Step 2 fields
  const [recoveryId, setRecoveryId] = useState('');
  const [code, setCode] = useState('');

  // Step 3 fields
  const [recoveryToken, setRecoveryToken] = useState('');
  const [verifiedSecondary, setVerifiedSecondary] = useState('');
  const [choice, setChoice] = useState('set_primary');  // | 'add_alternate'
  const [alternateEmail, setAlternateEmail] = useState('');

  const submitStart = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !city.trim() || !province.trim()
        || !phone.trim() || !secondaryEmail.trim()) {
      toast.error('Please fill in every field.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await authAPI.recoveryStart({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        city: city.trim(),
        province: province.trim(),
        phone: phone.trim(),
        secondary_email: secondaryEmail.trim().toLowerCase(),
      });
      // Always 200 — even on no-match. recovery_id will be missing if
      // the inputs didn't match a real account.
      if (data?.recovery_id) {
        setRecoveryId(data.recovery_id);
      }
      setStep(2);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(err?.response?.data?.detail || 'Too many attempts. Contact support.');
      } else {
        toast.error(err?.response?.data?.detail || 'Recovery could not be started.');
      }
    }
    setLoading(false);
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    if (!recoveryId) {
      // No recovery_id means /start didn't match the inputs.
      toast.error('Code is invalid or has expired. Start the recovery again.');
      setStep(1);
      return;
    }
    if (!code.trim()) {
      toast.error('Enter the 6-digit code we emailed you.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await authAPI.recoveryVerify({
        recovery_id: recoveryId,
        code: code.trim(),
      });
      setRecoveryToken(data.recovery_token);
      setVerifiedSecondary(data.secondary_email);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Code is invalid or has expired.');
    }
    setLoading(false);
  };

  const submitComplete = async (e) => {
    e.preventDefault();
    if (choice === 'add_alternate' && !alternateEmail.trim()) {
      toast.error('Enter the alternate email you want to verify.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await authAPI.recoveryComplete({
        recovery_token: recoveryToken,
        action: choice,
        alternate_email: choice === 'add_alternate' ? alternateEmail.trim().toLowerCase() : null,
      });
      // Section 5 — the email promotion completed, but this account has a
      // second factor. Do NOT go home; the session still waits on MFA. Bounce
      // to the login MFA challenge (same handoff as magic-link / reset-link).
      if (data?.mfa_required && data?.mfa_token) {
        toast.success('Identity confirmed. One more step — enter your authenticator code.');
        setTimeout(() => {
          window.location.replace(`/login?mfa=1&mfa_token=${encodeURIComponent(data.mfa_token)}`);
        }, 250);
        return;
      }
      if (choice === 'set_primary') {
        toast.success('Welcome back! Your sign-in email has been updated.');
      } else {
        toast.success('Welcome back! Check your inbox to verify the alternate email.');
      }
      // Hard navigate so the just-set auth cookie lands on the next GET.
      setTimeout(() => { window.location.replace('/'); }, 250);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not complete recovery.');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="account-recovery-flow">
      <button
        type="button"
        onClick={() => (step === 1 ? onBack() : setStep(step - 1))}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        data-testid="recovery-back-btn"
      >
        <ArrowLeft size={16} />
        {step === 1 ? 'Back to reset password' : 'Back'}
      </button>

      <div className="text-center space-y-1">
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
          <ShieldCheck size={22} />
        </div>
        <h2 className="font-heading text-xl font-bold">Recover your account</h2>
        <p className="text-sm text-muted-foreground">
          {step === 1 && 'Confirm your identity — every field must match what is on file.'}
          {step === 2 && `We sent a 6-digit code to ${secondaryEmail.toLowerCase()}.`}
          {step === 3 && 'Pick how you want to sign in going forward.'}
        </p>
      </div>

      {step === 1 && (
        <form onSubmit={submitStart} className="space-y-3" data-testid="recovery-step-1-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rec-fn" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">First name</Label>
              <Input id="rec-fn" value={firstName} onChange={(e) => setFirstName(e.target.value)} required data-testid="recovery-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-ln" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Last name</Label>
              <Input id="rec-ln" value={lastName} onChange={(e) => setLastName(e.target.value)} required data-testid="recovery-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rec-city" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">City</Label>
              <Input id="rec-city" value={city} onChange={(e) => setCity(e.target.value)} required data-testid="recovery-city" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rec-prov" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Province / State</Label>
              <Input id="rec-prov" value={province} onChange={(e) => setProvince(e.target.value)} required data-testid="recovery-province" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rec-phone" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Phone</Label>
            <Input id="rec-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="recovery-phone" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rec-sec" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">An alternate email already on your profile</Label>
            <Input id="rec-sec" type="email" autoCapitalize="off" autoCorrect="off" value={secondaryEmail} onChange={(e) => setSecondaryEmail(e.target.value)} required data-testid="recovery-secondary-email" />
            <p className="text-[11px] text-muted-foreground">We&apos;ll email a 6-digit code to confirm you own this address.</p>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full font-heading font-semibold"
            data-testid="recovery-start-btn"
          >
            {loading ? 'Verifying…' : 'Send verification code'}
          </Button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={submitVerify} className="space-y-3" data-testid="recovery-step-2-form">
          <div className="space-y-1.5">
            <Label htmlFor="rec-code" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Verification code</Label>
            <Input
              id="rec-code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              className="h-12 text-center tracking-widest text-lg"
              data-testid="recovery-code-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full font-heading font-semibold"
            data-testid="recovery-verify-btn"
          >
            {loading ? 'Verifying…' : 'Verify code'}
          </Button>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={submitComplete} className="space-y-3" data-testid="recovery-step-3-form">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-3 py-3 rounded-lg" data-testid="recovery-verified-banner">
            We verified you own <strong>{verifiedSecondary}</strong>.
          </div>
          <div className="space-y-2">
            <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors" data-testid="recovery-choice-set-primary">
              <input
                type="radio"
                name="recovery-choice"
                checked={choice === 'set_primary'}
                onChange={() => setChoice('set_primary')}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">Use <span className="text-nav-blue">{verifiedSecondary}</span> as my new sign-in email</div>
                <div className="text-xs text-muted-foreground mt-0.5">Recommended. You&apos;ll sign in with this email from now on.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors" data-testid="recovery-choice-add-alternate">
              <input
                type="radio"
                name="recovery-choice"
                checked={choice === 'add_alternate'}
                onChange={() => setChoice('add_alternate')}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">Verify a different alternate email instead</div>
                <div className="text-xs text-muted-foreground mt-0.5">We&apos;ll email a verification link to confirm the new address.</div>
              </div>
            </label>
            {choice === 'add_alternate' && (
              <Input
                type="email"
                placeholder="new-email@example.com"
                autoCapitalize="off"
                autoCorrect="off"
                value={alternateEmail}
                onChange={(e) => setAlternateEmail(e.target.value)}
                className="h-11"
                data-testid="recovery-alternate-input"
              />
            )}
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full font-heading font-semibold"
            data-testid="recovery-complete-btn"
          >
            {loading ? 'Signing in…' : 'Confirm & sign in'}
          </Button>
        </form>
      )}
    </div>
  );
}
